import { useState, useCallback } from 'react';
import { ColumnFilter } from '@insight2profit/api-connector/generated-service-lib/LookupService';
import { useAppContext } from 'context-providers';
import { AsyncTask, AsyncTaskState } from 'library/async';

interface RemoteTableData {
  continuationToken?: string;
  lookupValues: {}[];
}

interface GetLookupParams {
  clientId: string;
  tableId: string;
  tableVersion: number;
}

interface GetFilteredLookupParams extends GetLookupParams {
  filters: ColumnFilter[];
}

export function useLookup() {
  const { insightApi } = useAppContext();
  const [getLookupTableTask, setGetLookupTableTask] = useState<AsyncTask<RemoteTableData>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });
  const [getFilteredLookupTask, setGetFilteredLookupTask] = useState<AsyncTask<RemoteTableData>>({
    taskState: AsyncTaskState.NOT_STARTED,
  });

  const getLookupTable = useCallback(
    async ({ clientId, tableId, tableVersion }: GetLookupParams) => {
      setGetLookupTableTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });

      try {
        const tableMembersResponse = await insightApi.getLookupTableMembers(clientId, tableId, tableVersion);

        const remoteTableData: RemoteTableData = {
          lookupValues: tableMembersResponse,
        };

        setGetLookupTableTask({
          taskState: AsyncTaskState.COMPLETED,
          result: remoteTableData,
        });
      } catch (error: any) {
        setGetLookupTableTask({
          taskState: AsyncTaskState.ERRORED,
          error,
        });
      }
    },
    [setGetLookupTableTask, insightApi]
  );

  const getFilteredLookup = useCallback(
    async ({ clientId, tableId, tableVersion, filters }: GetFilteredLookupParams) => {
      setGetFilteredLookupTask({
        taskState: AsyncTaskState.IN_PROGRESS,
      });
      try {
        const results = await insightApi.getTableMembersWithFilters(clientId, tableId, tableVersion, filters);
        const remoteTableData: RemoteTableData = {
          lookupValues: results,
        };
        setGetFilteredLookupTask({
          taskState: AsyncTaskState.COMPLETED,
          result: remoteTableData,
        });
      } catch (error: any) {
        setGetFilteredLookupTask({
          taskState: AsyncTaskState.ERRORED,
          error,
        });
      }
    },
    [insightApi, setGetFilteredLookupTask]
  );

  // get another chunk.
  const onNextPage = useCallback(() => {}, []);

  return {
    onNextPage,
    getLookupTable,
    getLookupTableTask,
    getFilteredLookup,
    getFilteredLookupTask,
  };
}
